import React, { useState, useEffect, useContext } from 'react';
import { MyContext } from '../MainContext';
import { backendUrl } from '../utils/variables';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faBuilding, 
    faUsers, 
    faPlus,
    faCrown,
    faUser,
    faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useCustomAlert } from './CustomAlert';

const DepartmentList = () => {
    const { user } = useContext(MyContext);
    const { groupId } = useParams();
    const navigate = useNavigate();
    const { showAlert, AlertComponent } = useCustomAlert();
    
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userRole, setUserRole] = useState('member');
    const [showCreateModal, setShowCreateModal] = useState(false);

    useEffect(() => {
        if (user && user.user_id && groupId) {
            loadDepartments();
        }
    }, [user, groupId]);

    const loadDepartments = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/get_departments.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    group_id: groupId,
                    user_id: user.user_id
                })
            });

            const data = await response.json();
            if (data.success) {
                // Filter departments to only show those where user is a member or leader
                const allDepartments = data.data.departments || [];
                const userDepartments = allDepartments.filter(dept => dept.is_member);
                setDepartments(userDepartments);
                setUserRole(data.data.user_role);
            } else {
                setError(data.messages || 'Failed to load departments');
            }
        } catch (err) {
            setError('Failed to load departments');
            console.error('Error loading departments:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDepartmentClick = (department) => {
        navigate(`/group/${groupId}/department/${department.id}`);
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <div className="ms-3">Loading departments...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger" role="alert">
                {error}
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="mb-0">Departments</h4>
                    <small className="text-muted">Organize your group into specialized teams</small>
                </div>
                {userRole === 'admin' && (
                    <button 
                        className="btn btn-primary"
                        onClick={() => setShowCreateModal(true)}
                    >
                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                        Create Department
                    </button>
                )}
            </div>

            {departments.length === 0 ? (
                <div className="text-center py-5">
                    <FontAwesomeIcon icon={faBuilding} size="3x" className="text-muted mb-3" />
                    <h5 className="text-muted">No departments available</h5>
                    <p className="text-muted">
                        {userRole === 'admin' 
                            ? 'Create departments to organize your group into specialized teams.' 
                            : 'You are not a member of any departments in this group yet.'
                        }
                    </p>
                    {userRole === 'admin' && (
                        <button 
                            className="btn btn-primary"
                            onClick={() => setShowCreateModal(true)}
                        >
                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                            Create First Department
                        </button>
                    )}
                </div>
            ) : (
                <div className="row">
                    {departments.map((department) => (
                        <div key={department.id} className="col-md-6 col-lg-4 mb-3">
                            <div 
                                className="card h-100 department-card"
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleDepartmentClick(department)}
                            >
                                <div className="card-body d-flex flex-column">
                                    <div className="d-flex align-items-start mb-3">
                                        <div className="me-3">
                                            {department.logo_url ? (
                                                <img 
                                                    src={department.logo_url} 
                                                    alt={department.name}
                                                    className="rounded-circle"
                                                    style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <div 
                                                    className="rounded-circle d-flex align-items-center justify-content-center"
                                                    style={{ 
                                                        width: '50px', 
                                                        height: '50px', 
                                                        backgroundColor: '#e9ecef',
                                                        fontSize: '20px'
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faBuilding} />
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-grow-1">
                                            <h5 className="card-title mb-1">{department.name}</h5>
                                            <div className="d-flex align-items-center text-muted small">
                                                <FontAwesomeIcon icon={faUsers} className="me-1" />
                                                {department.member_count} member{department.member_count !== 1 ? 's' : ''}
                                            </div>
                                        </div>
                                        <FontAwesomeIcon icon={faArrowRight} className="text-muted" />
                                    </div>
                                    
                                    {department.description && (
                                        <p className="card-text text-muted small mb-3">
                                            {department.description.length > 100 
                                                ? department.description.substring(0, 100) + '...'
                                                : department.description
                                            }
                                        </p>
                                    )}
                                    
                                    <div className="mt-auto">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center">
                                                {department.leader_photo ? (
                                                    <img 
                                                        src={department.leader_photo} 
                                                        alt={department.leader_first_name}
                                                        className="rounded-circle me-2"
                                                        style={{ width: '24px', height: '24px', objectFit: 'cover' }}
                                                    />
                                                ) : (
                                                    <div 
                                                        className="rounded-circle d-flex align-items-center justify-content-center me-2"
                                                        style={{ 
                                                            width: '24px', 
                                                            height: '24px', 
                                                            backgroundColor: '#e9ecef',
                                                            fontSize: '10px'
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faUser} />
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="small fw-bold">
                                                        {department.leader_first_name} {department.leader_last_name}
                                                    </div>
                                                    <div className="small text-muted d-flex align-items-center">
                                                        <FontAwesomeIcon icon={faCrown} className="me-1" />
                                                        Leader
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {department.is_member && (
                                                <span className={`badge ${department.is_leader ? 'bg-warning' : 'bg-success'}`}>
                                                    {department.is_leader ? 'Leader' : 'Member'}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Department Modal */}
            {showCreateModal && (
                <CreateDepartmentModal 
                    groupId={groupId}
                    onClose={() => setShowCreateModal(false)}
                    onDepartmentCreated={loadDepartments}
                />
            )}

            {/* Custom Alert */}
            <AlertComponent />
        </div>
    );
};

// Create Department Modal Component
const CreateDepartmentModal = ({ groupId, onClose, onDepartmentCreated }) => {
    const { user } = useContext(MyContext);
    const { showAlert, AlertComponent } = useCustomAlert();
    const [formData, setFormData] = useState({
        name: '',
        description: ''
    });
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name.trim()) {
            setError('Department name is required');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            let logoUrl = null;
            
            if (logoFile) {
                const uploadFormData = new FormData();
                uploadFormData.append('image', logoFile);
                uploadFormData.append('user_id', user.user_id);
                uploadFormData.append('group_id', groupId);
                
                const uploadResponse = await fetch(`${backendUrl}/upload_to_cloudinary.php`, {
                    method: 'POST',
                    body: uploadFormData
                });
                
                const uploadData = await uploadResponse.json();
                
                if (uploadData.success && uploadData.data && uploadData.data.secure_url) {
                    logoUrl = uploadData.data.secure_url;
                } else {
                    setError(uploadData.messages || 'Failed to upload logo');
                    return;
                }
            }

            const departmentData = {
                group_id: groupId,
                user_id: user.user_id,
                name: formData.name.trim(),
                description: formData.description.trim(),
                logo_url: logoUrl
            };
            
            const response = await fetch(`${backendUrl}/create_department.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(departmentData)
            });

            const data = await response.json();

            if (data.success) {
                onDepartmentCreated();
                onClose();
                showAlert('Department created successfully!', 'success');
            } else {
                setError(data.messages || 'Failed to create department');
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error creating department:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Create Department</h5>
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={onClose}
                        ></button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}

                            <div className="mb-3">
                                <label htmlFor="name" className="form-label">Department Name *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    placeholder="Enter department name"
                                    maxLength={255}
                                    required
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="description" className="form-label">Description</label>
                                <textarea
                                    className="form-control"
                                    id="description"
                                    rows="3"
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    placeholder="Enter department description"
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="logo" className="form-label">Logo (Optional)</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    id="logo"
                                    accept="image/*"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            setLogoFile(file);
                                            const reader = new FileReader();
                                            reader.onload = (e) => setLogoPreview(e.target.result);
                                            reader.readAsDataURL(file);
                                        } else {
                                            setLogoFile(null);
                                            setLogoPreview(null);
                                        }
                                    }}
                                />
                                {logoPreview && (
                                    <div className="mt-2">
                                        <img 
                                            src={logoPreview} 
                                            alt="Preview" 
                                            className="img-thumbnail" 
                                            style={{ maxWidth: '100px', maxHeight: '100px' }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                type="button" 
                                className="btn btn-secondary" 
                                onClick={onClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className="btn btn-primary"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Creating...
                                    </>
                                ) : (
                                    'Create Department'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Custom Alert */}
            <AlertComponent />
        </div>
    );
};

export default DepartmentList;
